'use client';

import React, { useEffect, useState } from 'react';
import { CheckCircle, Upload, AlertCircle } from 'lucide-react';
import { printJobService } from '@/lib/supabaseService';
import Icon from '@/components/ui/AppIcon';


interface ActivityItem {
  id: string;
  label: string;
  detail: string;
  time: string;
  color: string;
  type: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

export default function DashboardActivity() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    printJobService.getRecentActivity().then((data) => {
      const mapped: ActivityItem[] = (data || []).map((row: Record<string, unknown>) => {
        const patient = row.patients as Record<string, unknown> | null;
        const patientName = patient?.name ? String(patient.name) : null;
        const fileName = String(row.file_name ?? '').replace('.slp3', '');

        if (row.qc_status === 'conforme' && row.validated_by) {
          return {
            id: String(row.id),
            type: 'validated',
            label: 'Fiche validée',
            detail: `${patientName ?? fileName} — validé par ${String(row.validated_by)}`,
            time: timeAgo(String(row.validated_at ?? row.imported_at ?? '')),
            color: 'var(--success)',
          };
        } else if (row.status === 'failed') {
          return {
            id: String(row.id),
            type: 'failed',
            label: 'Impression échouée',
            detail: `${fileName}`,
            time: timeAgo(String(row.imported_at ?? '')),
            color: 'var(--danger)',
          };
        } else {
          return {
            id: String(row.id),
            type: 'imported',
            label: 'Impression importée',
            detail: `${fileName}${patientName ? ` — ${patientName}` : ''}`,
            time: timeAgo(String(row.imported_at ?? '')),
            color: 'var(--primary)',
          };
        }
      });
      setActivities(mapped);
      setLoading(false);
    });
  }, []);

  const getIcon = (type: string) => {
    if (type === 'validated') return CheckCircle;
    if (type === 'failed') return AlertCircle;
    return Upload;
  };

  return (
    <div className="rounded-xl border h-full" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
      <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-sm font-600" style={{ color: 'var(--foreground)' }}>Activité récente</h3>
      </div>
      <div className="px-5 py-3 space-y-3">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={`act-skel-${i}`} className="flex items-start gap-3 animate-pulse">
              <div className="w-7 h-7 rounded-md flex-shrink-0" style={{ backgroundColor: 'var(--muted)' }} />
              <div className="flex-1">
                <div className="h-3 rounded w-2/3 mb-1" style={{ backgroundColor: 'var(--muted)' }} />
                <div className="h-2 rounded w-1/2" style={{ backgroundColor: 'var(--muted)' }} />
              </div>
            </div>
          ))
        ) : activities.length === 0 ? (
          <p className="text-xs py-4 text-center" style={{ color: 'var(--muted-foreground)' }}>Aucune activité récente</p>
        ) : (
          activities?.map((act) => {
            const Icon = getIcon(act?.type);
            return (
              <div key={act?.id} className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 rounded-md flex-shrink-0" style={{ backgroundColor: `${act?.color}18` }}>
                  <Icon size={13} style={{ color: act?.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{act?.label}</p>
                  <p className="text-2xs mt-0.5 truncate" style={{ color: 'var(--muted-foreground)' }}>{act?.detail}</p>
                </div>
                <span className="text-2xs flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>{act?.time}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}